'use client';

import { Button } from "@/components/ui/button";
import { useSubscriptionContext } from "@/hooks/use-subscription";
import { Plan, PlanFeatures, PlansType } from "@/lib/models/plan";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useMemo } from "react";
import { Card } from "./ui/card";
import { Skeleton } from "./ui/skeleton";

interface PricingCardProps {
      type: PlansType
      plan: Plan;
      onSubscribe: (plansType: PlansType) => void;
}

export const PricingCard = ({ type, plan, onSubscribe }: PricingCardProps) => {
      const router = useRouter();
      const locale = useLocale();
      const { prices, recurring, currency, isLoadingPrices, setPlanInterested } = useSubscriptionContext();
      const t = useTranslations('pricing_table');
      const tPlanFeature = useTranslations(`features`);

      const formattedPrice: string | null = useMemo(() => {

            if (type === 'free') {
                  return t('free2');
            }

            const price = prices.find(p => p.tier_info.code === type
                  && p.tier_info.recurring === recurring
                  && p.currency === currency);

            if (!price) {
                  return null;
            }

            if (price.amount === 0) {
                  return t('free2');
            }

            return (price.amount / 100).toLocaleString(locale, {
                  style: 'currency',
                  currency: price.currency,
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
            });
      }, [prices, recurring, type, currency]);

      const formattedMonthlyPrice: string | null = useMemo(() => {
            if (recurring !== 'year') {
                  return null;
            }

            const price = prices.find(p => p.tier_info.code === type
                  && p.tier_info.recurring === recurring
                  && p.currency === currency);

            if (!price) {
                  return null;
            }

            return ((price.amount / 12) / 100).toLocaleString(locale, {
                  style: 'currency',
                  currency: price.currency,
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
            });
      }, [prices, recurring, type, currency]);

      const getText = (feature: string) => {

            const key = feature as keyof PlanFeatures;

            if (typeof plan.features[key] === 'number') {
                  return tPlanFeature(`${type}.${key}`, { number: plan.features[key] as number });
            }

            switch (feature) {
                  case 'branding':
                  case 'metrics':
                        return tPlanFeature(`${type}.${key}`);
                  default:
                        return tPlanFeature(feature as keyof PlanFeatures);
            }
      }

      const onHandleSubscribe = () => {
            setPlanInterested(type);

            if (onSubscribe) {
                  onSubscribe(type);
            }
      }



      return (
            <Card
                  className={cn(
                        "relative rounded-2xl p-6 shadow-lg transition-transform duration-300 hover:-translate-y-3 group",
                        plan.recommended && "border-2 border-kiwilog"
                  )}
            >
                  {plan.recommended && (
                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-kiwilog text-primary-foreground text-sm font-medium rounded-full">
                              {t('more_popular')}
                        </span>
                  )}

                  <div className="text-center mb-6">
                        <h3 className="text-2xl font-bold mb-2">{t(plan.title)}</h3>
                        <p className="text-muted-foreground mb-4 h-10">{t(plan.description)}</p>
                        <div className="flex flex-col items-center justify-center gap-1">
                              {isLoadingPrices ? <Skeleton className="w-full h-10" /> : <div>
                                    <span className="text-4xl font-bold">{formattedPrice}</span>
                                    {type !== 'free' && <span className="text-muted-foreground">/{recurring === 'month' ? t('month') : t('year')}</span>}
                              </div>}
                              {formattedMonthlyPrice && <span className="text-base text-muted-foreground">{formattedMonthlyPrice} {t('by_month')}</span>}
                        </div>
                  </div>

                  <div className="flex flex-col">
                        <ul className="space-y-3 mb-6">
                              <li className="flex items-center gap-3">
                                    <Check className="h-5 w-5 text-kiwilog" />
                                    <span className="text-sm">{getText('maxProjects')}</span>
                              </li>
                              <li className="flex items-center gap-3">
                                    <Check className="h-5 w-5 text-kiwilog" />
                                    <span className="text-sm">{getText('maxPosts')}</span>

                              </li>
                              <li className="flex items-center gap-3">
                                    <Check className="h-5 w-5 text-kiwilog" />
                                    <span className="text-sm">{getText('maxCollaborators')}</span>
                              </li>
                              <li className="flex items-center gap-3">
                                    <Check className={cn("h-5 w-5", type === 'free' ? "text-muted-foreground/30" : "text-kiwilog")} />
                                    <span className={cn("text-sm", type === 'free' ? "text-muted-foreground" : "text-foreground")}>{getText('branding')}</span>

                              </li>
                              <li className="flex items-center gap-3">
                                    <Check className={cn("h-5 w-5", type === 'free' ? "text-muted-foreground/30" : "text-kiwilog")} />
                                    <span className={cn("text-sm", type === 'free' ? "text-muted-foreground" : "text-foreground")}>{getText('metrics')}</span>
                              </li>
                        </ul>
                        <div className="flex items-center justify-center">
                              <Link href="#table_features">
                                    <Button
                                          variant="ghost"
                                          size="sm"
                                    >
                                          {t('see_all_features')}
                                    </Button>
                              </Link>
                        </div>
                  </div>

                  <Button
                        onClick={onHandleSubscribe}
                        className={cn(
                              "w-full cursor-pointer",
                              plan.recommended ? "bg-kiwilog hover:bg-kiwilog/90" : "bg-kiwilog/50 hover:bg-kiwilog/90 group-hover:bg-kiwilog"
                        )}
                  >
                        {t('subscribe')}
                  </Button>
            </Card>
      );
};
